// ============================================================================
// Preview Device Context — lets blocks obey the editor's preview device
// instead of relying on the real browser viewport.
//
// null = production / non-editor → fall back to Chakra breakpoints as normal.
// ============================================================================

import { createContext, useContext } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';

type PreviewDevice = 'desktop' | 'mobile' | null;

const PreviewDeviceContext = createContext<PreviewDevice>(null);

export const PreviewDeviceProvider = PreviewDeviceContext.Provider;

export function usePreviewDevice(): PreviewDevice {
  return useContext(PreviewDeviceContext);
}

/**
 * Returns `base` on mobile viewports and `md` on desktop viewports.
 * When a preview device is active, the context value wins over the real viewport.
 */
export function useResponsiveValue<T>(base: T, md: T): T {
  const device = usePreviewDevice();

  // Chakra hook must be called unconditionally (rules of hooks)
  const viewportValue = useBreakpointValue({ base, md });

  if (device === 'mobile') return base;
  if (device === 'desktop') return md;
  return viewportValue ?? base;
}
